import React, { useState, useMemo, useEffect, useCallback } from "react";
import { CardNumberElement, CardMonthElement, CardYearElement, CardCvvElement } from "@recurly/react-recurly";
import { Country, State, ICountry, IState } from "country-state-city";
import classNames from "classnames";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxInput,
  ComboBoxOptions,
  ComboBoxOption,
} from "@ugitgud/legos/ui/form/combobox/ComboBox";
import { SvgArrowFillDown } from "@ugitgud/legos/ui/icon/SVG/ArrowFillDown";
import { FieldErrors } from "../utils/validateBillingForm";
import { getCurrencyFromCountry, SupportedCurrency } from "../utils/currencyHelpers";
import PayPalButton from "./PayPalButton";

type PaymentMethod = "credit-card" | "paypal" | null;

export interface FormState {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface RecurlyToken {
  id: string;
}

interface CheckoutFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  formData: FormState;
  setFormData: React.Dispatch<React.SetStateAction<FormState>>;
  onSaveCard: () => void;
  onPayPalToken?: (token: RecurlyToken) => void;
  isLoading: boolean;
  errors: string[];
  cardSaved: boolean;
  paypalSaved?: boolean;
  showPayPalOption?: boolean;
  fieldErrors?: FieldErrors;
  onFieldBlur?: (field: keyof FormState, value?: string) => void;
  onFieldChange?: (field: keyof FormState, value: string) => void;
  onCurrencyChange?: (currency: SupportedCurrency) => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  formRef,
  formData,
  setFormData,
  onSaveCard,
  onPayPalToken,
  isLoading,
  errors,
  cardSaved,
  paypalSaved = false,
  showPayPalOption = true,
  fieldErrors = {},
  onFieldBlur,
  onFieldChange,
  onCurrencyChange,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");

  // Memoized PayPal callbacks to prevent unnecessary re-renders
  const handlePayPalToken = useCallback(
    (token: RecurlyToken) => {
      onPayPalToken?.(token);
    },
    [onPayPalToken],
  );

  const handlePayPalError = useCallback((error: string) => {
    console.error("PayPal error:", error);
  }, []);

  // Auto-detect currency based on country and notify parent
  useEffect(() => {
    const detectedCurrency = getCurrencyFromCountry(formData.country);
    onCurrencyChange?.(detectedCurrency);
  }, [formData.country, onCurrencyChange]);

  // Get all countries
  const countries = useMemo(() => Country.getAllCountries(), []);

  // Get states/provinces for selected country
  const states = useMemo(() => {
    if (!formData.country) return [];
    return State.getStatesOfCountry(formData.country);
  }, [formData.country]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    const searchLower = countrySearch.toLowerCase();
    return countries.filter((country) => country.name.toLowerCase().includes(searchLower));
  }, [countries, countrySearch]);

  // Filter states based on search
  const filteredStates = useMemo(() => {
    if (!stateSearch.trim()) return states;
    const searchLower = stateSearch.toLowerCase();
    return states.filter((state) => state.name.toLowerCase().includes(searchLower));
  }, [states, stateSearch]);

  const handleInputChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;

    // Reset state when country changes
    if (field === "country") {
      setFormData((prev) => ({ ...prev, country: value, state: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    // Trigger debounced validation
    onFieldChange?.(field, value);
  };

  const handleBlur = (field: keyof FormState) => () => {
    onFieldBlur?.(field);
  };

  const inputErrorClasses = "border border-red-500";
  const errorTextClasses = "text-red-400 text-xs mt-1";

  const handleSaveCard = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveCard();
  };

  const baseRecurlyStyle = {
    fontColor: "#ffffff",
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    padding: "10px 12px",
  };

  const cardNumberStyle = {
    ...baseRecurlyStyle,
    placeholder: { color: "#5C5C8E", content: "Card number" },
  };

  const cardMonthStyle = {
    ...baseRecurlyStyle,
    placeholder: { color: "#5C5C8E", content: "MM" },
  };

  const cardYearStyle = {
    ...baseRecurlyStyle,
    placeholder: { color: "#5C5C8E", content: "YY" },
  };

  const cardCvvStyle = {
    ...baseRecurlyStyle,
    placeholder: { color: "#5C5C8E", content: "000" },
  };

  const getStateLabel = (countryCode: string) => {
    if (countryCode === "US") return "State";
    if (countryCode === "CA") return "Province";
    return "State / Province";
  };

  const getPostalLabel = (countryCode: string) => {
    if (!countryCode || countryCode === "US") return "Zip Code";
    return "Postal Code";
  };

  const getPostalPlaceholder = (countryCode: string) => {
    if (!countryCode || countryCode === "US") return "00000";
    return "Postal Code";
  };

  const stateLabel = getStateLabel(formData.country);
  const postalLabel = getPostalLabel(formData.country);
  const postalPlaceholder = getPostalPlaceholder(formData.country);

  const inputClasses =
    "w-full h-9 px-3 bg-purple-100 rounded-[3px] text-white text-xs placeholder:text-[#5C5C8E] placeholder:text-xs focus:outline-none";
  const labelClasses = "text-xs text-accent-gray-100 mb-1.5";
  const recurlyContainerClasses = "w-full bg-purple-100 rounded-[3px] h-[36px] overflow-hidden";

  return (
    <form ref={formRef} id="checkout-form" onSubmit={handleSaveCard}>
      {/* Payment Method Selection */}
      <div>
        {/* Section Header */}
        <div className="flex items-center p-4.5 bg-purple-200 rounded-t-[3px] border-b-[1px] border-purple-500">
          <span className="w-[3px] h-[18px] bg-accent-blue-400 rounded-[1px] mr-3" />
          <span className="text-sm font-semibold text-white">Select Payment Method</span>
        </div>

        {/* Payment Options Container */}
        <div className="bg-purple-400 rounded-b-[3px]">
          {/* Credit Card Option */}
          <label className="flex items-center p-4.5 border-b border-purple-500 cursor-pointer">
            <input
              type="radio"
              name="paymentMethod"
              value="credit-card"
              checked={paymentMethod === "credit-card"}
              onChange={() => setPaymentMethod("credit-card")}
              className="hidden"
            />
            <span
              className={classNames("w-3 h-3 border border-[#313160] rounded-full mr-3 flex-shrink-0 transition-all", {
                "bg-[#3273FA]": paymentMethod === "credit-card",
                "bg-gradient-to-t from-[#313160] to-[#191937]": paymentMethod !== "credit-card",
              })}
            />
            <span className="text-[12px] text-lavender-50">Credit card</span>
          </label>

          {/* Expanded Credit Card Form */}
          {paymentMethod === "credit-card" && (
            <div className="p-4.5 bg-[linear-gradient(227deg,#313160_-134.31%,#191937_100%)] border-b border-purple-500">
              {/* Card Information */}
              <div className="mb-4.5 pb-4.5 border-b border-purple-500">
                <div className="flex items-center mb-4">
                  <span className="w-[3px] h-[18px] bg-accent-blue-400 rounded-[1px] mr-3" />
                  <span className="text-sm font-bold">Card Information</span>
                </div>

                <div className="mb-1.5">
                  <label className={labelClasses}>Name on card</label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <input
                      type="text"
                      data-recurly="first_name"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleInputChange("firstName")}
                      onBlur={handleBlur("firstName")}
                      className={classNames(inputClasses, { [inputErrorClasses]: fieldErrors.firstName })}
                    />
                    {fieldErrors.firstName && <p className={errorTextClasses}>{fieldErrors.firstName}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      data-recurly="last_name"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleInputChange("lastName")}
                      onBlur={handleBlur("lastName")}
                      className={classNames(inputClasses, { [inputErrorClasses]: fieldErrors.lastName })}
                    />
                    {fieldErrors.lastName && <p className={errorTextClasses}>{fieldErrors.lastName}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-4">
                  <div className="sm:col-span-1 col-span-full flex flex-col gap-x-1.5">
                    <label className={labelClasses}>Card number</label>
                    <div className={recurlyContainerClasses}>
                      <CardNumberElement style={cardNumberStyle} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>Exp</label>
                    <div className="flex items-center gap-2">
                      <div className={`${recurlyContainerClasses} flex-1`}>
                        <CardMonthElement style={cardMonthStyle} />
                      </div>
                      <span className="text-gray-500">/</span>
                      <div className={`${recurlyContainerClasses} flex-1`}>
                        <CardYearElement style={cardYearStyle} />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>CVV</label>
                    <div className={recurlyContainerClasses}>
                      <CardCvvElement style={cardCvvStyle} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Information */}
              <div>
                <div className="flex items-center mb-4.5">
                  <span className="w-[3px] h-[18px] bg-accent-blue-400 rounded-[1px] mr-3" />
                  <span className="text-sm font-bold">Billing Information</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>Address line 1</label>
                    <input
                      type="text"
                      data-recurly="address1"
                      placeholder="Street name and number"
                      value={formData.address1}
                      onChange={handleInputChange("address1")}
                      onBlur={handleBlur("address1")}
                      className={classNames(inputClasses, { [inputErrorClasses]: fieldErrors.address1 })}
                    />
                    {fieldErrors.address1 && <p className={errorTextClasses}>{fieldErrors.address1}</p>}
                  </div>
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>Address line 2</label>
                    <input
                      type="text"
                      data-recurly="address2"
                      placeholder="Apt, suite, etc. (optional)"
                      value={formData.address2}
                      onChange={handleInputChange("address2")}
                      className={inputClasses}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>Country</label>
                    <input type="hidden" data-recurly="country" value={formData.country} />
                    <ComboBox
                      value={
                        formData.country
                          ? { label: countries.find((c) => c.isoCode === formData.country)?.name || "", value: formData.country }
                          : undefined
                      }
                      onChange={(option) => {
                        if (option && !Array.isArray(option)) {
                          const value = option.value || "";
                          setFormData((prev) => ({ ...prev, country: value, state: "" }));
                          onFieldChange?.("country", value);
                          onFieldBlur?.("country", value);
                          setCountrySearch("");
                        }
                      }}
                      nullable
                      className={classNames("h-9 rounded-[3px]", { [inputErrorClasses]: fieldErrors.country })}
                    >
                      <ComboBoxButton
                        as="div"
                        className="w-full h-full flex items-center justify-between px-3 text-xs rounded-[3px]"
                      >
                        <ComboBoxInput
                          placeholder="Country"
                          value={countrySearch || countries.find((c) => c.isoCode === formData.country)?.name || ""}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          className={classNames(
                            "!bg-transparent hover:!bg-transparent h-full !p-0 text-xs flex-1 min-w-0 outline-none placeholder:text-[#5C5C8E]",
                            {
                              "text-[#5C5C8E]": !formData.country && !countrySearch,
                              "text-white": formData.country || countrySearch,
                            },
                          )}
                        />
                        <SvgArrowFillDown className="w-3 h-3 text-lavender-50 pointer-events-none flex-shrink-0" />
                      </ComboBoxButton>
                      <ComboBoxOptions className="max-h-[200px] overflow-y-auto text-xs">
                        {filteredCountries.map((country: ICountry) => (
                          <ComboBoxOption key={country.isoCode} option={{ label: country.name, value: country.isoCode }}>
                            {() => <span className="text-xs">{country.name}</span>}
                          </ComboBoxOption>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="px-3 py-2 text-xs text-lavender-50">No countries found</div>
                        )}
                      </ComboBoxOptions>
                    </ComboBox>
                    {fieldErrors.country && <p className={errorTextClasses}>{fieldErrors.country}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>City</label>
                    <input
                      type="text"
                      data-recurly="city"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleInputChange("city")}
                      onBlur={handleBlur("city")}
                      className={classNames(inputClasses, { [inputErrorClasses]: fieldErrors.city })}
                    />
                    {fieldErrors.city && <p className={errorTextClasses}>{fieldErrors.city}</p>}
                  </div>
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>{stateLabel}</label>
                    <input type="hidden" data-recurly="state" value={formData.state} />
                    {formData.country && states.length === 0 ? (
                      <>
                        <input
                          type="text"
                          placeholder={stateLabel}
                          value={formData.state}
                          onChange={handleInputChange("state")}
                          onBlur={handleBlur("state")}
                          className={classNames(inputClasses, { [inputErrorClasses]: fieldErrors.state })}
                        />
                        {fieldErrors.state && <p className={errorTextClasses}>{fieldErrors.state}</p>}
                      </>
                    ) : (
                      <>
                        <ComboBox
                          value={
                            formData.state
                              ? { label: states.find((s) => s.isoCode === formData.state)?.name || "", value: formData.state }
                              : undefined
                          }
                          onChange={(option) => {
                            if (option && !Array.isArray(option)) {
                              const value = option.value || "";
                              setFormData((prev) => ({ ...prev, state: value }));
                              onFieldChange?.("state", value);
                              onFieldBlur?.("state", value);
                              setStateSearch("");
                            }
                          }}
                          nullable
                          className={classNames("h-9 rounded-[3px]", {
                            "opacity-50 cursor-not-allowed": !formData.country,
                            [inputErrorClasses]: fieldErrors.state,
                          })}
                        >
                          <ComboBoxButton as="div" className="w-full h-full flex items-center justify-between px-3 text-xs">
                            <ComboBoxInput
                              placeholder={stateLabel}
                              value={stateSearch || states.find((s) => s.isoCode === formData.state)?.name || ""}
                              onChange={(e) => setStateSearch(e.target.value)}
                              disabled={!formData.country}
                              className={classNames(
                                "!bg-transparent hover:!bg-transparent h-full !p-0 text-xs flex-1 min-w-0 outline-none placeholder:text-[#5C5C8E]",
                                {
                                  "text-[#5C5C8E]": !formData.state && !stateSearch,
                                  "text-white": formData.state || stateSearch,
                                },
                              )}
                            />
                            <SvgArrowFillDown className="w-3 h-3 text-lavender-50 pointer-events-none flex-shrink-0" />
                          </ComboBoxButton>
                          <ComboBoxOptions className="max-h-[200px] overflow-y-auto text-xs">
                            {filteredStates.map((state: IState) => (
                              <ComboBoxOption key={state.isoCode} option={{ label: state.name, value: state.isoCode }}>
                                {() => <span className="text-xs">{state.name}</span>}
                              </ComboBoxOption>
                            ))}
                            {filteredStates.length === 0 && (
                              <div className="px-3 py-2 text-xs text-lavender-50">No results found</div>
                            )}
                          </ComboBoxOptions>
                        </ComboBox>
                        {fieldErrors.state && <p className={errorTextClasses}>{fieldErrors.state}</p>}
                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-x-1.5">
                    <label className={labelClasses}>{postalLabel}</label>
                    <input
                      type="text"
                      data-recurly="postal_code"
                      placeholder={postalPlaceholder}
                      value={formData.postalCode}
                      onChange={handleInputChange("postalCode")}
                      onBlur={handleBlur("postalCode")}
                      className={classNames(inputClasses, { [inputErrorClasses]: fieldErrors.postalCode })}
                    />
                    {fieldErrors.postalCode && <p className={errorTextClasses}>{fieldErrors.postalCode}</p>}
                  </div>
                </div>
              </div>

              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                  {errors.map((error, index) => (
                    <div key={index} className="text-red-400 text-sm">
                      {error}
                    </div>
                  ))}
                </div>
              )}

              {/* Save Card Button */}
              {!cardSaved && (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full mt-4 p-2.5 bg-accent-blue-400 text-white rounded-[3px] text-[13px] font-bold cursor-pointer transition-all hover:bg-accent-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Processing..." : "Save Card"}
                </button>
              )}
              {cardSaved && (
                <div className="mt-6 py-3.5 px-6 bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg text-sm font-semibold text-center">
                  Card Saved Successfully
                </div>
              )}
            </div>
          )}

          {/* PayPal Option */}
          {showPayPalOption && (
            <>
              <label className="flex items-center p-4.5 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="paypal"
                  checked={paymentMethod === "paypal"}
                  onChange={() => setPaymentMethod("paypal")}
                  className="hidden"
                />
                <span
                  className={classNames("w-3 h-3 border border-[#313160] rounded-full mr-3 flex-shrink-0 transition-all", {
                    "bg-[#3273FA]": paymentMethod === "paypal",
                    "bg-gradient-to-t from-[#313160] to-[#191937]": paymentMethod !== "paypal",
                  })}
                />
                <span className="text-[12px] text-lavender-50">PayPal</span>
              </label>

              {paymentMethod === "paypal" && (
                <div className="p-6 bg-[linear-gradient(227deg,#313160_-134.31%,#191937_100%)] border-t border-purple-500">
                  {!paypalSaved ? (
                    <>
                      <p className="text-lavender-50 text-sm mb-4 text-center">
                        Click the PayPal button below to link your account.
                      </p>
                      <PayPalButton onToken={handlePayPalToken} onError={handlePayPalError} />
                      {errors.length > 0 && (
                        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                          {errors.map((error, index) => (
                            <div key={index} className="text-red-400 text-sm">
                              {error}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="py-3.5 px-6 bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg text-sm font-semibold text-center">
                      PayPal Linked Successfully
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </form>
  );
};

export default CheckoutForm;
